import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Shield, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Privacy & Consent profile section.
 * - POPIA Consent: read-only (revoking would mean deleting the account)
 * - Information Sharing Consent: user can toggle on/off any time
 */
export default function ConsentSection() {
  const [loading, setLoading] = useState(true);
  const [popia, setPopia] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${API_URL}/api/consent/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        setPopia(d.popia);
        setSharing(d.info_sharing);
      }
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggleSharing = async (next) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${API_URL}/api/consent/info-sharing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ accepted: !!next, platform: 'web' }),
      });
      if (!r.ok) throw new Error('Could not update consent');
      const d = await r.json();
      setSharing(d.info_sharing);
      toast.success(next ? 'Information-sharing consent enabled' : 'Information-sharing consent revoked');
    } catch (e) {
      toast.error(e?.message || 'Could not update consent');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <Card data-testid="privacy-consent-card">
      <CardHeader className="pb-2 flex flex-row items-center gap-2">
        <Shield className="w-5 h-5 text-sky-600" />
        <CardTitle className="text-base font-manrope">Privacy &amp; Consent</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading consent status…
          </div>
        ) : (
          <>
            {/* POPIA */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">POPIA Consent</span>
                    {popia ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="popia-status-badge">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Accepted
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-300">Pending</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Required to use BodyCorp365 — cannot be withdrawn without closing your account.
                  </p>
                </div>
              </div>
              {popia && (
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-slate-600">
                  <div><span className="text-slate-400">Accepted: </span>{fmt(popia.accepted_at)}</div>
                  <div><span className="text-slate-400">Platform: </span>{popia.platform}</div>
                  <div><span className="text-slate-400">Policy: </span>v{popia.policy_version}</div>
                  <div><span className="text-slate-400">Terms: </span>v{popia.terms_version}</div>
                </div>
              )}
            </div>

            {/* Info Sharing */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-medium text-slate-900">Information Sharing</div>
                  <p className="text-xs text-slate-500 mt-1">
                    Allow BodyCorp365 to share your personal information with your Management Company, Insurance Company and authorised suppliers where reasonably necessary for community administration, maintenance and insurance claims.
                  </p>
                  {sharing?.updated_at && (
                    <p className="text-xs text-slate-400 mt-2" data-testid="info-sharing-updated-at">
                      Last updated: {fmt(sharing.updated_at)}
                    </p>
                  )}
                </div>
                <Switch
                  checked={!!sharing?.accepted}
                  onCheckedChange={toggleSharing}
                  disabled={saving}
                  data-testid="info-sharing-switch"
                />
              </div>
            </div>

            <div className="text-xs text-slate-500">
              Review the{' '}
              <Link to="/privacy-policy" className="text-sky-600 underline" data-testid="profile-privacy-link">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms-of-service" className="text-sky-600 underline" data-testid="profile-terms-link">
                Terms &amp; Conditions
              </Link>
              .
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchStatus}
              data-testid="refresh-consent-btn"
            >
              Refresh
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
